import { Box, Button, Divider, Stack, useTheme } from "@mui/material";
import { PlusIcon } from "lucide-react";
import type { FC } from "react";
import { useDocumentListQuery } from "web_myplann/Api/Document/useDocumentListQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentItemRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentItemRedesign";
import { DocumentListSkeleton } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentListSkeleton";
import { DocumentsEmptyState } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentsEmptyState";
import { FolderItemRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/FolderItemRedesign";
import { RedesignListBackground } from "web_myplann/Shared/Components/Redesign/RedesignListBackground";

type Props = {
	onAddDocument: () => void;
};

export const DocumentListRedesign: FC<Props> = ({ onAddDocument }) => {
	const theme = useTheme();
	const { t } = useResource();
	const { data, isLoading } = useDocumentListQuery();

	if (isLoading) {
		return <DocumentListSkeleton />;
	}

	const isEmpty = data?.documents?.length === 0 && data?.tags?.length === 0;

	if (isEmpty) {
		return <DocumentsEmptyState onAddDocument={onAddDocument} />;
	}

	return (
		<RedesignListBackground
			data-testid="document-list-page-redesign"
			pt={theme.spacingTokens.sm}
		>
			<Box bgcolor="background.paper">
				<Stack divider={<Divider redesigned />}>
					{data?.tags?.map((tag) => (
						<FolderItemRedesign key={tag.title} item={tag} />
					))}
					{data?.documents?.map((document) => (
						<DocumentItemRedesign key={document.id} item={document} />
					))}
				</Stack>
			</Box>

			<Box px={theme.spacingTokens.lg} mt={theme.spacingTokens.xl}>
				<Button
					variant="secondary"
					startIcon={<PlusIcon size={theme.iconSize.md} />}
					onClick={onAddDocument}
					sx={{ color: theme.palette.blue[600] }}
				>
					{t(Resources.Portfolio.List.Redesign.Documents.addDocument)}
				</Button>
			</Box>
		</RedesignListBackground>
	);
};
